import ReactGA from 'react-ga4';
import ReactUA from 'react-ga';

import { SubmitOutcome } from '@/utils/submitOutcome';

declare global {
  interface Window {
    dataLayer?: Array<unknown>;
  }
}

/**
 * Event name shared with the embedded widget analytics bridge
 * (action-widgets embedded-action listener), so one GA4 key event or GTM
 * trigger covers landing pages and widgets.
 */
export const CONVERSION_EVENT = 'muster_form_submit';

export interface ConversionDetails {
  actionType: 'email' | 'phone' | 'petition' | 'signup';
  formType: 'action_center' | 'signup_form';
  outcome: SubmitOutcome;
}

/**
 * Initializes Google Tag Manager by injecting the GTM script snippet into the page.
 *
 * @param {string} containerId - The GTM container ID (e.g. "GTM-XXXX").
 */
export const initGoogleTagManager = (containerId: string): void => {
  const script = document.createElement('script');

  script.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${containerId}');
  `;
  document.head.appendChild(script);
};

/**
 * Conversion params shared by every customer analytics sink (GA4, GTM, Meta),
 * so customers segment conversions the same way everywhere. Never add PII.
 */
const conversionParams = ({
  actionType,
  formType,
  outcome,
}: ConversionDetails) => ({
  muster_action_type: actionType,
  muster_form_type: formType,
  muster_outcome: outcome,
});

/**
 * Sends a Meta Pixel Lead event for a 2xx form submission.
 *
 * @param {string} pixelId - The customer's Meta Pixel ID.
 * @param {boolean} pixelEnabled - Whether the customer enabled the Meta Pixel.
 * @param {ConversionDetails} details - What was submitted and its outcome.
 */
export const metaPixelTrackLead = (
  pixelId: string | undefined,
  pixelEnabled: boolean | undefined,
  details: ConversionDetails
): void => {
  if (!pixelId || !pixelEnabled) return;

  import('react-facebook-pixel')
    .then(module => module.default)
    .then(ReactPixel => {
      ReactPixel.track('Lead', conversionParams(details));
    })
    .catch(() => {
      // An analytics failure must never break the submission flow.
    });
};

/**
 * Sends a simple page view event to Google Analytics.
 *
 * This conditionally uses the old Universal Analytics or the new Google Analytics 4.
 *
 * @param {string} googleAnalyticsId - The Google Analytics ID to send the event to.
 */
export const googleAnalyticsPageview = (googleAnalyticsId: string) => {
  if (googleAnalyticsId.startsWith('UA')) {
    ReactUA.initialize(googleAnalyticsId);
    ReactUA.pageview(window.location.pathname + window.location.search);
  } else {
    // gtag('config') sends its own page_view by default; disable it so the
    // explicit send below is the only one.
    ReactGA.initialize(googleAnalyticsId, {
      gtagOptions: { send_page_view: false },
    });
    ReactGA.send({
      hitType: 'pageview',
      page: window.location.pathname + window.location.search,
      title: window.document.title,
    });
  }
};

/**
 * Sends the customer's conversion event for a 2xx form submission: a GA4
 * event for G- IDs, a dataLayer event for GTM- IDs. Universal Analytics IDs
 * are ignored (UA no longer processes data).
 *
 * Params must never contain PII.
 *
 * @param {string} googleAnalyticsId - The customer's GA4 measurement ID or GTM container ID.
 * @param {boolean} googleAnalyticsEnabled - Whether the customer enabled Google Analytics.
 * @param {ConversionDetails} details - What was submitted and its outcome.
 */
export const trackCustomerConversion = (
  googleAnalyticsId: string | undefined,
  googleAnalyticsEnabled: boolean | undefined,
  details: ConversionDetails
): void => {
  if (!googleAnalyticsId || !googleAnalyticsEnabled || googleAnalyticsId.startsWith('UA')) return;

  const params = conversionParams(details);

  try {
    if (googleAnalyticsId.startsWith('GTM-')) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: CONVERSION_EVENT,
        ...params,
      });
    } else {
      ReactGA.event(CONVERSION_EVENT, params);
    }
  } catch {
    // An analytics failure must never break the submission flow.
  }
};
